import { Accessor, children, Component, createContext, createEffect, createRoot, createSignal, createUniqueId, getOwner, JSX, JSXElement, onCleanup, onMount, runWithOwner, Show, Signal, useContext } from "solid-js";
import { Dynamic, Portal, render } from "solid-js/web";
import Flexbox from "../st-flexbox/st-flexbox";
import Loader from "../st-loader/st-loader";
import { captureContext, delay, dom } from "common";

import style from './st-modal.module.css';

export class STModalProps {
    show!: Signal<boolean>;
    class?: string;
    title?: string;
    ariaLabel?: string;
    draggable?: boolean;
    children: JSXElement;
}

class ModalContext {
    isOpen: Accessor<boolean>;
    setOpen: (open: boolean) => void;

    constructor(isOpen: Accessor<boolean>, setOpen: (open: boolean) => void) {
        this.isOpen = isOpen;
        this.setOpen = setOpen;
    }

    close = () => {
        this.setOpen(false);
    }
}

const emptyImage = new Image();
emptyImage.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';

const context = createContext<ModalContext>();

const ModalProvider = context.Provider;

export const useModalContext = () => {
    const ctx = useContext(context);
    return ctx;
}

export const requireModalContext = () => {
    const ctx = useModalContext();
    if (!ctx) throw new Error("Modal context is not available");
    return ctx;
}

export default function STModal(props: STModalProps) {
    return (
        <ModalProvider value={new ModalContext(props.show[0], props.show[1])}>
            <STModalInternal {...props} />
        </ModalProvider>
    )
}

function STModalInternal(props: STModalProps) {
    const modal = requireModalContext();

    const [offset, setOffset] = createSignal({ x: 0, y: 50 });
    const content = children(() => props.children)
    const titleId = `st-modal-title-${createUniqueId()}`;

    let dialog: HTMLDivElement;
    let observer: ResizeObserver;
    function initModal(d: HTMLDivElement) {
        dialog = d;
        dialog.addEventListener('close', ev => {
            if (modal.isOpen()) {
                modal.close();
            }
        });

        dialog.addEventListener('mousedown', (e) => {
            if (e.target === dialog) {
                modal.close();
            }
        });
        
        requestAnimationFrame(() => {
            let prevWidth = dialog.clientWidth;
            let prevHeight = dialog.clientHeight;

            observer = new ResizeObserver((entries) => {
                const [modalEntry] = entries;

                const currentOffset = offset();

                const newOffsetX = (currentOffset.x / prevWidth) * modalEntry.contentRect.width;
                const newOffsetY = (currentOffset.y / prevHeight) * modalEntry.contentRect.height;
                
                setOffset({ x: newOffsetX, y: newOffsetY });

                prevWidth = modalEntry.contentRect.width;
                prevHeight = modalEntry.contentRect.height;
            })
            observer.observe(dialog);
        })
    }

    let dragStartX = 0;
    let dragStartY = 0;

    const onDragStart = (ev: DragEvent) => {
        ev.dataTransfer?.setDragImage(emptyImage, 0, 0);

        dragStartX = ev.clientX;
        dragStartY = ev.clientY;
    }

    const onDrag = (ev: DragEvent) => {
        const dx = ev.clientX - dragStartX;
        const dy = ev.clientY - dragStartY;

        dragStartX = ev.clientX;
        dragStartY = ev.clientY;

        setOffset({ x: offset().x + dx, y: offset().y + dy });
    }

    onCleanup(() => {
        observer?.disconnect();
    })

    onMount(() => {
        createEffect(() => {
            if (!modal.isOpen()) return;
            if (!dialog) return;

            let zIndex = 1024;
            let el = dialog.parentElement as Element | null | undefined;

            while (el = el?.previousElementSibling) {
                if (el instanceof HTMLElement && dom.isElementInViewport(el)) {
                    const z = parseInt(getComputedStyle(el).zIndex);
                    if (z < 10_000 && z > zIndex) zIndex = z;
                }
            }


            dialog.parentElement?.style.setProperty('z-index', `${zIndex + 1}`);

            if (!dialog.contains(document.activeElement)) {
                // Give a bit of time for the dev to select a field
                setTimeout(() => {
                    const content = dialog.querySelector(`.${style.content}`);
                    if (!content) return;

                    const elToFocus = Array.from(content.querySelectorAll('input,textarea,button,[role="button"]'))
                        .filter(el => !el.ariaDisabled && !el.getAttribute('disabled'))[0];

                    if (elToFocus instanceof HTMLInputElement) {
                        elToFocus.focus();
                    }
                }, 100);
            }
        })
    })

    return <Show when={modal.isOpen()}>
        <Portal>
            <div
                class={style['dialog-container']}
                ref={initModal}
                role="dialog"
                aria-modal="true"
                aria-labelledby={props.title ? titleId : undefined}
                aria-label={!props.title ? props.ariaLabel : undefined}
            >
                <Flexbox dir="vertical" class={`${style.dialog} ${props.class ?? ''}`} style={{
                    "margin-left": offset().x * 2 + 'px',
                    "margin-top": offset().y + 'px'
                }}>
                    <Show when={props.title}>
                        <Flexbox middle padded dir='horizontal' class={style.header} >
                            <Flexbox.Item as="h2" id={titleId} auto class={style.title} draggable="true" onDragStart={onDragStart} onDrag={onDrag}>{props.title}</Flexbox.Item>
                            <Flexbox.Item as="span" role="button" class="btn btn-link st-tool-button" onClick={() => modal.close()} aria-label="Close">
                                <i class="fa fa-times" role="presentation"></i>
                            </Flexbox.Item>
                        </Flexbox>
                    </Show>
                    <Flexbox dir="vertical" auto class={style.content}>
                        {content()}
                    </Flexbox>
                </Flexbox>
            </div>
        </Portal>
    </Show>
}

STModal.defaultProps = new STModalProps();

export function showModal(Content: Component, config?: Omit<STModalProps, 'children' | 'show'>) {
    const [show, setShow] = createSignal(true);
    const container = document.createElement('div');
    document.body.appendChild(container);

    const modal = () => (
        <STModal show={[show, setShow]} {...(config ?? {})}>
            <Content />
        </STModal>
    )

    const owner = getOwner();
    const dispose = owner 
        ? render(() => runWithOwner(owner, modal), container)
        : render(modal, container);

    return () => {
        setShow(false);
        dispose();
        document.body.removeChild(container);
    };

}

export async function showDialog<T>(content: (resolve: (value: T) => void, reject: (err: any) => void) => JSXElement, config?: Omit<STModalProps, 'children' | 'show'>): Promise<T | undefined> {
    const [show, setShow] = createSignal(true);
    const container = document.createElement('div');
    document.body.appendChild(container);

    let cleanup: () => void = () => {};

    try {
        let resolved = false;
        const result = await new Promise<T | undefined>((promiseResolve, promiseReject) => {
            createEffect(() => {
                if (!resolved && !show()) {
                    promiseReject();
                }
            });

            const resolve = (value: T | undefined) => {
                resolved = true;
                setShow(false);
                promiseResolve(value);
            }

            const reject = (err: any) => {
                resolved = true;
                setShow(false);
                promiseReject(err);
            }
    
            const dialog = () => (
                <STModal show={[show, setShow]} {...(config ?? {})}>
                    {content(resolve, reject)}
                </STModal>
            )
    
            cleanup = render(dialog, container);
        })

        return result;
    } catch(err) {
        throw err;
    } finally {
        setShow(false);
        cleanup();
        document.body.removeChild(container);
    }
}

export function showProgressDialog<T>(label: string | undefined, promise: Promise<T>) {
    const minDuration = 1250; // ms
    const start = Date.now();

    return showDialog((resolve, reject) => {
        (async () => {
            try {
                const value = await promise;
                const elapsed = Date.now() - start;

                const waitMs = Math.max(minDuration - elapsed, 0);
                await delay(waitMs);
                resolve(value);
            } catch (err) {
                const elapsed = Date.now() - start;
                const waitMs = Math.max(minDuration - elapsed, 0);
                await delay(waitMs);
                reject(err);
            }
        })();

        return (
            <div>
                <Loader size="large" label={label ?? ""} />
            </div>
        );
    });
}
